import {
  compact,
  first,
  flatten,
  intersectionwith,
  unionby,
} from '@quromedical/utils';
import type { CodeableConcept, Coding, Identifier } from 'fhir/r4';

import { type IdentifierCode, system as quroSystem } from '../coding';
import type { Voidable } from './id';

/**
 * Get the codes from a codable concept the TCode type will handle the mapping
 */
export const getCodes = <TCode = string>(
  codeableConcept?: CodeableConcept,
): TCode[] => {
  if (!codeableConcept) {
    return [];
  }

  const { coding = [] } = codeableConcept;
  const codes = coding.map((c) => c.code as TCode | undefined);
  return compact(codes);
};

/**
 * Gets the codes from a codable concept list the TCode type will handle the mapping
 */
export const mergeCodes = <TCode extends string = string>(
  codeableConcepts: CodeableConcept[] = [],
): TCode[] => {
  const codeMatrix = codeableConcepts.map((code) => getCodes<TCode>(code));
  return flatten(codeMatrix);
};

/**
 * Find the identifier with the provider code
 */
export const findIdentifier = (
  code: string,
  identifier?: Identifier[],
): Identifier | undefined =>
  identifier?.find((i) => i.type?.coding?.find((c) => c.code === code));

/**
 * Get the value for the `identifier` with the given `code`
 */
export const getIdentifier = (
  code: string,
  identifier?: Identifier[],
): string | undefined => findIdentifier(code, identifier)?.value;

/**
 * Removes the identifier with the given `code` from the existing ones
 */
export const removeIdentifier = (
  code: IdentifierCode,
  identifier?: Identifier[],
): Identifier[] => {
  if (!identifier) {
    return [];
  }

  return identifier.filter(
    (i) => !i.type?.coding?.find((c) => c.code === code),
  );
};

/**
 * Removes the multiple identifiers with the given `codes` from the existing ones
 */
export const removeMultipleIdentifiers = (
  codes: IdentifierCode[],
  identifier?: Identifier[],
): Identifier[] => {
  if (!identifier) {
    return [];
  }
  if (!codes.length) {
    return identifier;
  }

  return codes.reduce((acc, code) => removeIdentifier(code, acc), identifier);
};

/**
 * Create a `Coding`
 * @param code
 * @param display defaults to code if not provided
 * @param extension the extension for the code, defaults to undefined
 * @param system the system for the code defaulted to the Quro Medical system
 * @param version the version for the code ie ICD-10 or ICD-9
 * @returns
 */
export const createCoding = <TCode extends string = string>(
  code: TCode,
  display: string = code,
  system = quroSystem,
  version?: string,
): RequiredCoding<TCode> => ({
  code,
  system,
  display,
  version,
});

/**
 * Create a `CodeableConcept` with a single code
 *
 * @param code
 * @param text defaults to code if not provided
 * @param display
 * @param codeExtension the extension for the code within the codable concept, defaults to undefined
 * @param system the system for the codable concept defaulted to the Quro Medical system
 * @returns
 */
export const createCodeableConcept = (
  code: string,
  text: string = code,
  display?: string,
  system = quroSystem,
  version?: string,
): RequiredCodeableConcept => {
  const coding = createCoding(code, display, system, version);

  return {
    text,
    coding: [coding],
  };
};

/**
 * Filters the `resourceCoding` that appear in `supportedCodings` if multiple codings
 *  are in `supportedCodings` the return `undefined`
 *
 * @param supportedCodings This could be for example an array of DeviceCodes
 * @param resourceCoding the codings to search from for example device codings
 * @returns one coding from  `supportedCodings` and undefined if none or multiple found
 * @example
 *  import { DeviceUseStatementCode, system } from 'coding';
 *
 *  const supportedCodes = Object.values(DeviceUseStatementCode)
 *  const codings = [{ code: DeviceUseStatementCode.patch, system }]
 *  const coding = getSupportedCode(supportedCodes)
 *
 *  // returns { code: DeviceUseStatementCode.patch, system }
 * @example
 *  import { DeviceUseStatementCode, system } from 'coding';
 *
 *  const supportedCodes = Object.values(DeviceUseStatementCode)
 *  const codings = [
 *    { code: DeviceUseStatementCode.patch, system },
 *    { code: DeviceUseStatementCode.relay, system }
 *   ]
 *  const coding = getSupportedCode(supportedCodes)
 *
 *  // returns undefined because codings cannot be patch and relay at the same time
 */
export const getSupportedCode = <T extends string>(
  supportedCodes: T[],
  resourceCodings?: Coding[],
): Voidable<RequiredCoding<T>> => {
  if (!resourceCodings?.length) {
    return undefined;
  }
  const codes = intersectionwith(
    resourceCodings,
    supportedCodes,
    (resourceCoding, supportedCode) => supportedCode === resourceCoding.code,
  );

  const uniqueCodes = unionby(codes, 'code');
  const [uniqueCode] = uniqueCodes;
  if (uniqueCodes.length !== 1 || !uniqueCode?.system) {
    return undefined;
  }

  return uniqueCode as RequiredCoding<T>;
};

/**
 * Check if the `searchCode` exists in the `codings` array
 */
export const isCodingInList = (
  searchCode: string,
  codings?: Coding[],
): boolean => {
  const foundCode = codings?.find((c) => c.code === searchCode);
  return !!foundCode;
};

export type RequiredCoding<T extends string = string> = Coding &
  Required<Pick<Coding, 'system'>> & {
    code: T;
  };

export type RequiredCodeableConcept<T extends string = string> =
  CodeableConcept & {
    coding: RequiredCoding<T>[];
  };

/**
 * Get the codes from a codeable concept list the TCode type will handle the mapping
 */
export const getAllCodesFromCodableConcepts = <TCode extends string = string>(
  codeableConcepts: CodeableConcept[] | undefined,
): TCode[] | undefined => {
  if (!codeableConcepts || codeableConcepts.length === 0) {
    return undefined;
  }

  return mergeCodes<TCode>(codeableConcepts);
};

/**
 * Get the first codes from a codable concept list the TCode type will handle the mapping
 */
export const getFirstCodeFromCodableConcepts = <TCode extends string = string>(
  codeableConcepts: CodeableConcept[] | undefined,
): TCode | undefined => {
  const codes = getAllCodesFromCodableConcepts<TCode>(codeableConcepts);

  return first(codes);
};

/**
 * Get the first codes from a codable concept list the TCode type will handle the mapping
 */
export const getDisplayFromCodableConcepts = (
  codeableConcepts: CodeableConcept[] | undefined,
): string | undefined => {
  if (!codeableConcepts || codeableConcepts.length === 0) {
    return undefined;
  }

  const displays = codeableConcepts.map((code) =>
    code.coding?.map((coding) => coding.display),
  );
  const texts = compact(flatten(displays));
  return first(texts);
};

/**
 * Get all the codes from within the given codable concepts
 */
export const getCodingsFromCodableConcepts = <TCode>(
  codeableConcepts: CodeableConcept[] | undefined,
): TCode[] | undefined => {
  if (!codeableConcepts || codeableConcepts.length === 0) {
    return undefined;
  }

  const codes = codeableConcepts.map<TCode[]>(
    (codeableConcept) => codeableConcept.coding as TCode[],
  );
  return flatten(codes);
};

export const getMatchingCodeFromCodeableConcepts = <T extends string>(
  supportedCodes: T[] | Record<string, T>,
  codeableConcepts: CodeableConcept[] = [],
): T | undefined => {
  if (!codeableConcepts.length) {
    return undefined;
  }

  const codes = new Set(
    Array.isArray(supportedCodes)
      ? supportedCodes
      : Object.values(supportedCodes),
  );

  const matchingCodeableConcepts = codeableConcepts.filter((concept) =>
    concept.coding?.some((c) => c.code && codes.has(c.code as T)),
  );

  const codeableConcept = first(matchingCodeableConcepts)?.coding;
  const matchingCoding = codeableConcept?.find(
    (coding) => coding.code && codes.has(coding.code as T),
  );
  const matchedCode = matchingCoding?.code;

  return matchedCode as T | undefined;
};
